import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LuDownload } from "react-icons/lu";
import { saveAs } from "file-saver";
import { BeatLoader } from "react-spinners";
import { ITransaction } from "../types";
import { getAllTransactionsByAdmin } from "../services/transactionService";
import { Table } from "../components/table/table";
import TransactionDropdownActions from "../components/actions/transaction-dropdown";
import TransactionDetailsModal from "../components/modals/transaction-details";

const LuDownloadIcon = LuDownload as React.ElementType;

interface TransactionsProps {
  searchQuery?: string;
}

const Transactions: React.FC<TransactionsProps> = ({ searchQuery }) => {
  const {
    data: transactions,
    isLoading,
    error,
  } = useQuery<ITransaction[]>({
    queryKey: ["transactions", searchQuery],
    queryFn: () => getAllTransactionsByAdmin(searchQuery),
  });

  const [selectedTransaction, setSelectedTransaction] =
    useState<ITransaction | null>(null);

  const handleView = (transaction: ITransaction) => {
    setSelectedTransaction(transaction);
  };

  const downloadCSV = () => {
    if (!transactions || transactions.length === 0) return;

    const headers = ["From", "To", "Amount", "Type", "Status", "Created At"];

    const rows = transactions.map((transaction: ITransaction) => [
      `"${transaction.fromUser?.firstName ?? "N/A"} ${
        transaction.fromUser?.lastName ?? "N/A"
      }"`,
      `"${transaction.toUser?.firstName ?? "N/A"} ${
        transaction.toUser?.lastName ?? "N/A"
      }"`,
      `"${transaction.amount}"`,
      `"${transaction.transactionType}"`,
      `"${transaction.transactionStatus}"`,
      `"${new Date(transaction.createdAt).toLocaleDateString()}"`,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "transactions.csv");
  };

  const columns = [
    {
      header: "From",
      accessorKey: "fromUser",
      cell: ({ row }: { row: { original: ITransaction } }) => {
        const user = row.original.fromUser;
        return user ? (
          <p className="text-sm font-medium text-gray-800">
            {user.firstName} {user.lastName}
          </p>
        ) : (
          <p className="text-sm text-gray-600">N/A</p>
        );
      },
    },
    {
      header: "To",
      accessorKey: "to",
      cell: ({ row }: { row: { original: ITransaction } }) => {
        const user = row.original.toUser;
        return user ? (
          <p className="text-sm font-medium text-gray-800">
            {user.firstName} {user.lastName}
          </p>
        ) : (
          <p className="text-sm text-gray-600">N/A</p>
        );
      },
    },
    {
      header: "Amount (₦)",
      accessorKey: "amount",
      cell: ({ row }: { row: { original: ITransaction } }) => (
        <p className="text-sm text-gray-700">{row.original.amount}</p>
      ),
    },
    {
      header: "Type",
      accessorKey: "transactionType",
      cell: ({ row }: { row: { original: ITransaction } }) => (
        <span className="text-sm text-blue-500 capitalize">
          {row.original.transactionType}
        </span>
      ),
    },
    {
      header: "Status",
      accessorKey: "transactionStatus",
      cell: ({ row }: { row: { original: ITransaction } }) => (
        <span
          className={`text-sm capitalize ${
            row.original.transactionStatus === "completed"
              ? "text-emerald-500"
              : row.original.transactionStatus === "pending"
              ? "text-amber-500"
              : "text-rose-500"
          }`}
        >
          {row.original.transactionStatus}
        </span>
      ),
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ row }: { row: { original: ITransaction } }) => (
        <p className="text-sm text-gray-600">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </p>
      ),
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: ITransaction } }) => (
        <TransactionDropdownActions
          transaction={row.original}
          onView={handleView}
        />
      ),
    },
  ];

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );

  if (error)
    return <p className="text-rose-500 text-sm">Error loading transactions.</p>;

  return (
    <div className="">
      <div className="px-2 py-4 md:px-4 md:py-2 flex items-center justify-between">
        <h2 className="text-xl">Transactions</h2>
        <div className="flex items-center gap-2">
          <div
            onClick={downloadCSV}
            className="px-4 py-2 bg-transparent border flex items-center justify-between space-x-2 text-gray-800 text-sm rounded-full hover:opacity-50 cursor-pointer transition"
          >
            <LuDownloadIcon />
            <span>CSV</span>
          </div>
        </div>
      </div>
      <Table columns={columns} data={transactions || []} />

      {selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
};

export default Transactions;
